import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { api } from "../api";

function clean(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function freshKey(prefix) {
  try {
    if (
      typeof window !== "undefined" &&
      window.crypto &&
      typeof window.crypto
        .randomUUID === "function"
    ) {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
  } catch {
    // Random UUID is convenience only; server authority is unchanged.
  }

  return `${prefix}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;
}

function humanType(value) {
  return clean(value)
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (letter) =>
        letter.toUpperCase()
    );
}

function sceneLabel(scene) {
  const order =
    Number(scene?.order) || 0;

  const heading =
    (
      Array.isArray(
        scene?.elements
      )
        ? scene.elements
        : []
    ).find(
      (element) =>
        clean(element?.type) ===
        "scene_heading"
    );

  const title =
    clean(heading?.text);

  return title
    ? `Scene ${order}: ${title}`
    : `Scene ${order}`;
}

function elementLabel(element) {
  const type =
    humanType(element?.type);

  const text =
    clean(element?.text);

  return text
    ? `${type}: ${text.slice(0, 72)}`
    : type;
}

function formatWhen(value) {
  const raw =
    clean(value);

  if (!raw) return "";

  const date =
    new Date(raw);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return raw;
  }

  return date.toLocaleString();
}

export default function ScreenplayProposalRoundWorkspace({
  projectId,
  writingMode = "solo",
  access = null,
  active = false,
  isFR = false,
  onOpenReview,
}) {
  const mode =
    clean(writingMode)
      .toLowerCase();

  const collaborationEnabled =
    mode !== "solo";

  const isOwner =
    access?.relationship ===
      "owner" ||
    access?.canManageProject ===
      true;

  const [loading, setLoading] =
    useState(false);
  const [working, setWorking] =
    useState(false);
  const [error, setError] =
    useState("");
  const [notice, setNotice] =
    useState("");
  const [phase, setPhase] =
    useState("none");
  const [
    proposalRound,
    setProposalRound,
  ] = useState(null);
  const [context, setContext] =
    useState(null);
  const [
    contributions,
    setContributions,
  ] = useState([]);
  const [
    myContributions,
    setMyContributions,
  ] = useState([]);
  const [
    openingReason,
    setOpeningReason,
  ] = useState("");
  const [closesAt, setClosesAt] =
    useState("");
  const [
    closingReason,
    setClosingReason,
  ] = useState("");
  const [
    proposalType,
    setProposalType,
  ] = useState(
    "script_element_revision"
  );
  const [
    selectedSceneId,
    setSelectedSceneId,
  ] = useState("");
  const [
    selectedElementId,
    setSelectedElementId,
  ] = useState("");
  const [
    elementText,
    setElementText,
  ] = useState("");
  const [
    sceneDraftElements,
    setSceneDraftElements,
  ] = useState([]);
  const [
    showExpandedCanonContext,
    setShowExpandedCanonContext,
  ] = useState(false);

  const openKeyRef =
    useRef(
      freshKey(
        "screenplay-round-open"
      )
    );

  const submissionKeyRef =
    useRef(
      freshKey(
        "screenplay-contribution"
      )
    );

  const loadRound =
    useCallback(
      async () => {
        const id =
          clean(projectId);

        if (
          !active ||
          !id ||
          !collaborationEnabled
        ) {
          setLoading(false);
          setPhase("none");
          setProposalRound(null);
          setContext(null);
          setContributions([]);
          setMyContributions([]);
          setError("");
          return;
        }

        try {
          setLoading(true);
          setError("");

          const current =
            await api
              .getCurrentScreenplayProposalRound(
                id
              );

          const round =
            current?.proposalRound ||
            null;

          setPhase(
            clean(
              current?.phase
            ) || "none"
          );
          setProposalRound(round);

          if (!round) {
            setContext(null);
            setContributions([]);
            setMyContributions([]);
            return;
          }

          if (isOwner) {
            const inventory =
              await api
                .getScreenplayProposalRoundContributions(
                  id,
                  round.id
                );

            setContributions(
              Array.isArray(
                inventory
                  ?.contributions
              )
                ? inventory
                    .contributions
                : []
            );

            setContext(null);
            setMyContributions([]);
          } else {
            const payload =
              await api
                .getScreenplayProposalRoundContext(
                  id,
                  round.id
                );

            setContext(
              payload?.context ||
              null
            );
            setContributions([]);
            setMyContributions(
              Array.isArray(
                payload
                  ?.myContributions
              )
                ? payload
                    .myContributions
                : []
            );
          }
        } catch (err) {
          setError(
            clean(err?.message) ||
            (
              isFR
                ? "Impossible de charger le Proposal Round."
                : "Could not load the Proposal Round."
            )
          );
        } finally {
          setLoading(false);
        }
      },
      [
        active,
        collaborationEnabled,
        isFR,
        isOwner,
        projectId,
      ]
    );

  useEffect(() => {
    loadRound();
  }, [loadRound]);

  const proposalRoundId =
    clean(
      proposalRound?.id
    );

  useEffect(() => {
    setShowExpandedCanonContext(
      false
    );
  }, [proposalRoundId]);

  const scenes =
    useMemo(
      () =>
        Array.isArray(
          context?.scenes
        )
          ? context.scenes
          : [],
      [context]
    );

  const recentCanonContextScenes =
    scenes.length <= 2
      ? scenes
      : scenes.slice(-2);

  const expandedCanonContextScenes =
    scenes.length <= 5
      ? scenes
      : scenes.slice(-5);

  const visibleCanonContextScenes =
    showExpandedCanonContext
      ? expandedCanonContextScenes
      : recentCanonContextScenes;

  const selectedScene =
    useMemo(
      () =>
        scenes.find(
          (scene) =>
            clean(
              scene?.sceneId
            ) ===
            clean(
              selectedSceneId
            )
        ) || null,
      [
        scenes,
        selectedSceneId,
      ]
    );

  const selectedElement =
    useMemo(
      () =>
        (
          Array.isArray(
            selectedScene
              ?.elements
          )
            ? selectedScene
                .elements
            : []
        ).find(
          (element) =>
            clean(
              element?.elementId
            ) ===
            clean(
              selectedElementId
            )
        ) || null,
      [
        selectedElementId,
        selectedScene,
      ]
    );

  function chooseScene(sceneId) {
    const id =
      clean(sceneId);

    const scene =
      scenes.find(
        (row) =>
          clean(
            row?.sceneId
          ) === id
      ) || null;

    setSelectedSceneId(id);

    const elements =
      Array.isArray(
        scene?.elements
      )
        ? scene.elements
        : [];

    const firstElement =
      elements[0] || null;

    setSelectedElementId(
      clean(
        firstElement?.elementId
      )
    );

    setElementText(
      firstElement?.text ==
        null
        ? ""
        : String(
            firstElement.text
          )
    );

    setSceneDraftElements(
      elements.map(
        (element) => ({
          elementId:
            clean(
              element?.elementId
            ),
          type:
            clean(
              element?.type
            ),
          text:
            element?.text ==
              null
              ? ""
              : String(
                  element.text
                ),
        })
      )
    );
  }

  useEffect(() => {
    if (
      scenes.length === 0
    ) {
      setSelectedSceneId("");
      setSelectedElementId("");
      setElementText("");
      setSceneDraftElements([]);
      return;
    }

    const stillExists =
      scenes.some(
        (scene) =>
          clean(
            scene?.sceneId
          ) ===
          clean(
            selectedSceneId
          )
      );

    if (!stillExists) {
      chooseScene(
        scenes[0].sceneId
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scenes,
    selectedSceneId,
  ]);

  const pendingContributions =
    contributions.filter(
      (contribution) =>
        clean(
          contribution
            ?.decisionStatus
        ) === "pending"
    );

  const myPendingContributions =
    useMemo(
      () =>
        myContributions
          .filter(
            (contribution) =>
              clean(
                contribution
                  ?.decisionStatus
              ) === "pending"
          ),
      [myContributions]
    );

  const selectedOwnSubmission =
    useMemo(
      () => {
        const sceneId =
          clean(
            selectedSceneId
          );

        const elementId =
          clean(
            selectedElementId
          );

        return (
          [
            ...myPendingContributions,
          ]
            .reverse()
            .find(
              (contribution) => {
                if (
                  clean(
                    contribution
                      ?.contributionType
                  ) !==
                  proposalType
                ) {
                  return false;
                }

                if (
                  clean(
                    contribution
                      ?.scope
                      ?.contentUnitId
                  ) !==
                  sceneId
                ) {
                  return false;
                }

                if (
                  proposalType ===
                  "script_element_revision"
                ) {
                  return (
                    clean(
                      contribution
                        ?.scope
                        ?.subUnitId
                    ) ===
                    elementId
                  );
                }

                return !clean(
                  contribution
                    ?.scope
                    ?.subUnitId
                );
              }
            ) ||
          null
        );
      },
      [
        myPendingContributions,
        proposalType,
        selectedElementId,
        selectedSceneId,
      ]
    );

  const elementChanged =
    Boolean(
      selectedElement &&
      String(
        selectedElement?.text ??
        ""
      ) !==
        String(
          elementText ?? ""
        )
    );

  const sceneChanged =
    Boolean(
      selectedScene &&
      sceneDraftElements.some(
        (element, index) =>
          String(
            element?.text ?? ""
          ) !==
          String(
            selectedScene
              ?.elements
              ?.[index]
              ?.text ??
            ""
          )
      )
    );

  useEffect(() => {
    if (
      !selectedOwnSubmission
    ) {
      return;
    }

    const snapshot =
      selectedOwnSubmission
        ?.contentSnapshot;

    if (
      proposalType ===
      "script_element_revision"
    ) {
      setElementText(
        snapshot?.text == null
          ? ""
          : String(
              snapshot.text
            )
      );
      return;
    }

    if (
      Array.isArray(
        snapshot?.elements
      )
    ) {
      setSceneDraftElements(
        snapshot.elements
          .map(
            (element) => ({
              elementId:
                clean(
                  element
                    ?.elementId
                ),
              type:
                clean(
                  element?.type
                ),
              text:
                element?.text ==
                null
                  ? ""
                  : String(
                      element.text
                    ),
            })
          )
      );
    }
  }, [
    proposalType,
    selectedOwnSubmission,
  ]);

  async function openRound(event) {
    event?.preventDefault?.();

    const reason =
      clean(openingReason);

    if (!reason) {
      setError(
        isFR
          ? "Indiquez ce que vous souhaitez que les membres proposent."
          : "State what you want members to propose."
      );
      return;
    }

    let closesAtIso = null;

    if (closesAt) {
      const parsed =
        new Date(closesAt);

      if (
        Number.isNaN(
          parsed.getTime()
        ) ||
        parsed.getTime() <=
          Date.now()
      ) {
        setError(
          isFR
            ? "Choisissez une date de clôture future."
            : "Choose a future closing time."
        );
        return;
      }

      closesAtIso =
        parsed.toISOString();
    }

    try {
      setWorking(true);
      setError("");
      setNotice("");

      await api
        .openScreenplayProposalRound(
          projectId,
          {
            openingReason:
              reason,
            closesAt:
              closesAtIso,
            idempotencyKey:
              openKeyRef
                .current,
          }
        );

      openKeyRef.current =
        freshKey(
          "screenplay-round-open"
        );

      setOpeningReason("");
      setClosesAt("");
      setNotice(
        isFR
          ? "Proposal Round ouvert."
          : "Proposal Round opened."
      );

      await loadRound();
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’ouvrir le Proposal Round."
            : "Could not open the Proposal Round."
        )
      );
    } finally {
      setWorking(false);
    }
  }

  async function closeRound(event) {
    event?.preventDefault?.();

    const reason =
      clean(closingReason);

    if (
      !proposalRound?.id ||
      !reason
    ) {
      setError(
        isFR
          ? "Indiquez pourquoi le Proposal Round est clôturé."
          : "Record why the Proposal Round is being closed."
      );
      return;
    }

    if (
      pendingContributions
        .length > 0
    ) {
      setError(
        isFR
          ? "Examinez d’abord les contributions en attente."
          : "Review the pending contributions before closing the Proposal Round."
      );
      return;
    }

    try {
      setWorking(true);
      setError("");
      setNotice("");

      await api
        .closeScreenplayProposalRound(
          projectId,
          proposalRound.id,
          {
            expectedRevision:
              proposalRound
                .revision,
            closingReason:
              reason,
          }
        );

      setClosingReason("");
      setNotice(
        isFR
          ? "Proposal Round clôturé."
          : "Proposal Round closed."
      );

      await loadRound();
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible de clôturer le Proposal Round."
            : "Could not close the Proposal Round."
        )
      );
    } finally {
      setWorking(false);
    }
  }

  function chooseElement(
    elementId
  ) {
    const id =
      clean(elementId);

    const element =
      (
        Array.isArray(
          selectedScene
            ?.elements
        )
          ? selectedScene
              .elements
          : []
      ).find(
        (row) =>
          clean(
            row?.elementId
          ) === id
      ) || null;

    setSelectedElementId(id);
    setElementText(
      element?.text ==
        null
        ? ""
        : String(
            element.text
          )
    );
  }

  function updateSceneElement(
    index,
    value
  ) {
    setSceneDraftElements(
      (current) =>
        current.map(
          (
            element,
            elementIndex
          ) =>
            elementIndex ===
            index
              ? {
                  ...element,
                  text:
                    value,
                }
              : element
        )
    );
  }

  async function submitProposal(
    event
  ) {
    event?.preventDefault?.();

    if (
      !proposalRound?.id ||
      !selectedScene
    ) {
      setError(
        isFR
          ? "Choisissez d’abord une scène."
          : "Choose a scene first."
      );
      return;
    }

    if (
      selectedOwnSubmission
    ) {
      setError(
        isFR
          ? "Cette proposition a déjà été envoyée et attend la décision du propriétaire."
          : "This proposal has already been submitted and is awaiting the Owner's decision."
      );
      return;
    }

    let request;

    if (
      proposalType ===
      "script_element_revision"
    ) {
      if (
        !selectedElement ||
        !elementChanged
      ) {
        setError(
          isFR
            ? "Modifiez l’élément avant d’envoyer la proposition."
            : "Change the Script Element before submitting the proposal."
        );
        return;
      }

      request = {
        proposalType:
          "script_element_revision",
        expectedDraftRevision:
          Number(
            proposalRound
              ?.scope
              ?.baseContentRevision
          ),
        targetSceneId:
          selectedScene
            .sceneId,
        targetElementId:
          selectedElement
            .elementId,
        content: {
          elementId:
            selectedElement
              .elementId,
          type:
            selectedElement
              .type,
          text:
            elementText,
        },
      };
    } else {
      if (!sceneChanged) {
        setError(
          isFR
            ? "Modifiez la scène avant d’envoyer l’alternative."
            : "Change the scene before submitting the alternative."
        );
        return;
      }

      request = {
        proposalType:
          "scene_alternative",
        expectedDraftRevision:
          Number(
            proposalRound
              ?.scope
              ?.baseContentRevision
          ),
        targetSceneId:
          selectedScene
            .sceneId,
        targetElementId:
          "",
        content: {
          sceneId:
            selectedScene
              .sceneId,
          elements:
            sceneDraftElements
              .map(
                (element) => ({
                  elementId:
                    element
                      .elementId,
                  type:
                    element.type,
                  text:
                    element.text,
                })
              ),
        },
      };
    }

    try {
      setWorking(true);
      setError("");
      setNotice("");

      await api
        .submitScreenplayContribution(
          projectId,
          proposalRound.id,
          request,
          submissionKeyRef
            .current
        );

      submissionKeyRef.current =
        freshKey(
          "screenplay-contribution"
        );

      setNotice(
        isFR
          ? "Proposition envoyée au propriétaire pour examen."
          : "Proposal submitted for the Owner to review."
      );

      await loadRound();
    } catch (err) {
      setError(
        clean(err?.message) ||
        (
          isFR
            ? "Impossible d’envoyer cette proposition."
            : "Could not submit this proposal."
        )
      );
    } finally {
      setWorking(false);
    }
  }

  if (!collaborationEnabled) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 20,
        }}
      >
        <h3
          className="ns-card-title"
          style={{
            marginTop: 0,
          }}
        >
          Proposal Round
        </h3>

        <p
          className="ns-card-meta"
          style={{
            marginBottom: 0,
          }}
        >
          {isFR
            ? "Les Proposal Rounds sont disponibles en mode Collaboratif ou Groupe."
            : "Proposal Rounds are available in Collaborative or Group Writing modes."}
        </p>
      </section>
    );
  }

  return (
    <section
      className="ns-card"
      aria-label="Screenplay Proposal Round"
      style={{
        padding: 20,
        borderRadius: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "flex-start",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              color: "#dc2626",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
            }}
          >
            COLLABORATION
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin:
                "5px 0 6px",
            }}
          >
            Proposal Round
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {isOwner
              ? (
                  isFR
                    ? "Invitez des propositions sans ouvrir le Script Workspace ni abandonner votre autorité de décision."
                    : "Invite proposals without opening Script Workspace or giving up your final decision authority."
                )
              : (
                  isFR
                    ? "Examinez le contexte gelé du scénario et soumettez une proposition au propriétaire."
                    : "Review the frozen screenplay context and submit a proposal to the Owner."
                )}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            loading ||
            working
          }
          onClick={
            loadRound
          }
        >
          {loading
            ? (
                isFR
                  ? "Chargement…"
                  : "Loading…"
              )
            : (
                isFR
                  ? "Actualiser"
                  : "Refresh"
              )}
        </button>
      </div>

      {error ? (
        <div
          role="alert"
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: "#fef2f2",
            color: "#991b1b",
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: "#f0fdf4",
            color: "#166534",
            fontWeight: 700,
          }}
        >
          {notice}
        </div>
      ) : null}

      {loading ? null : phase ===
        "opening" ? (
        <div
          style={{
            marginTop: 16,
          }}
        >
          {isFR
            ? "Le Proposal Round est en cours d’ouverture. Actualisez dans un instant."
            : "The Proposal Round is being opened. Refresh in a moment."}
        </div>
      ) : !proposalRound ? (
        isOwner ? (
          <form
            onSubmit={
              openRound
            }
            style={{
              marginTop: 20,
              display: "grid",
              gap: 18,
              width: "100%",
              maxWidth: 820,
            }}
          >
            <label
              style={{
                display: "grid",
                gap: 8,
              }}
            >
              <strong>
                {isFR
                  ? "Que souhaitez-vous que les membres proposent ?"
                  : "What should members propose?"}
              </strong>

              <textarea
                className="ns-input"
                rows={4}
                maxLength={1200}
                value={
                  openingReason
                }
                disabled={
                  working
                }
                onChange={(
                  event
                ) =>
                  setOpeningReason(
                    event.target
                      .value
                  )
                }
                style={{
                  marginTop: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  minHeight: 128,
                  resize: "vertical",
                }}
              />
            </label>

            <label
              style={{
                display: "grid",
                gap: 8,
                justifyItems: "start",
              }}
            >
              <strong>
                {isFR
                  ? "Clôture prévue (facultatif)"
                  : "Planned closing time (optional)"}
              </strong>

              <input
                className="ns-input"
                type="datetime-local"
                value={closesAt}
                disabled={working}
                onChange={(
                  event
                ) =>
                  setClosesAt(
                    event.target
                      .value
                  )
                }
                style={{
                  marginTop: 0,
                  width: "100%",
                  maxWidth: 420,
                  boxSizing: "border-box",
                }}
              />
            </label>

            <div>
              <button
                className="ns-button ns-button-primary"
                type="submit"
                disabled={
                  working ||
                  !clean(
                    openingReason
                  )
                }
              >
                {working
                  ? (
                      isFR
                        ? "Ouverture…"
                        : "Opening…"
                    )
                  : (
                      isFR
                        ? "Ouvrir le Proposal Round"
                        : "Open Proposal Round"
                    )}
              </button>
            </div>
          </form>
        ) : (
          <div
            className="ns-card-meta"
            style={{
              marginTop: 18,
            }}
          >
            {isFR
              ? "Aucun Proposal Round n’est ouvert pour le moment."
              : "No Proposal Round is open right now."}
          </div>
        )
      ) : (
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gap: 16,
          }}
        >
          <div
            style={{
              padding: 17,
              borderRadius: 14,
              background: "#fff1bd",
              border:
                "1px solid rgba(184,134,11,0.52)",
              boxShadow:
                "inset 5px 0 0 #d4a72c, 0 1px 3px rgba(15,23,42,0.06)",
            }}
          >
            <strong>
              {proposalRound
                .openingReason}
            </strong>

            <div
              className="ns-card-meta"
              style={{
                marginTop: 6,
              }}
            >
              {isFR
                ? "Révision gelée du scénario"
                : "Frozen screenplay revision"}
              :{" "}
              {Number(
                proposalRound
                  ?.scope
                  ?.baseContentRevision
              )}
              {proposalRound
                ?.closesAt
                ? ` · ${
                    isFR
                      ? "Clôture"
                      : "Closes"
                  } ${formatWhen(
                    proposalRound
                      .closesAt
                  )}`
                : ""}
            </div>
          </div>

          {!isOwner &&
          myPendingContributions
            .length > 0 ? (
            <div
              role="status"
              style={{
                padding: 14,
                borderRadius: 14,
                background: "#f0fdf4",
                border:
                  "1px solid rgba(22,101,52,0.20)",
                color: "#166534",
              }}
            >
              <strong>
                {isFR
                  ? `${
                      myPendingContributions
                        .length
                    } proposition(s) envoyée(s) · En attente de l’examen du propriétaire`
                  : `${
                      myPendingContributions
                        .length
                    } proposal(s) submitted · Awaiting Owner review`}
              </strong>

              <div
                className="ns-card-meta"
                style={{
                  marginTop: 5,
                  color: "inherit",
                }}
              >
                {isFR
                  ? "Une proposition déjà envoyée reste visible mais verrouillée pendant qu’elle est en attente. Choisissez une autre scène ou un autre élément du script pour faire une autre proposition."
                  : "A submitted target remains visible but locked while it is pending. Choose another Scene or Script Element if you want to make another proposal."}
              </div>
            </div>
          ) : null}

          {isOwner ? (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <strong>
                  {isFR
                    ? `${pendingContributions.length} contribution(s) en attente`
                    : `${pendingContributions.length} pending contribution(s)`}
                </strong>

                {pendingContributions
                  .length > 0 ? (
                  <button
                    className="ns-button ns-button-primary"
                    type="button"
                    onClick={() =>
                      onOpenReview?.()
                    }
                  >
                    {isFR
                      ? "Examiner et décider"
                      : "Review and Decide"}
                  </button>
                ) : null}
              </div>

              {pendingContributions
                .length === 0 ? (
                <form
                  onSubmit={
                    closeRound
                  }
                  style={{
                    display: "grid",
                    gap: 12,
                    width: "100%",
                    maxWidth: 820,
                  }}
                >
                  <label
                    style={{
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <strong>
                      {isFR
                        ? "Motif de clôture"
                        : "Closing reason"}
                    </strong>

                    <textarea
                      className="ns-input"
                      rows={3}
                      maxLength={1200}
                      value={
                        closingReason
                      }
                      disabled={
                        working
                      }
                      onChange={(
                        event
                      ) =>
                        setClosingReason(
                          event.target
                            .value
                        )
                      }
                      style={{
                        marginTop: 0,
                        width: "100%",
                        boxSizing: "border-box",
                        minHeight: 110,
                        resize: "vertical",
                      }}
                    />
                  </label>

                  <div>
                    <button
                      className="ns-button ns-button-outline"
                      type="submit"
                      disabled={
                        working ||
                        !clean(
                          closingReason
                        )
                      }
                    >
                      {isFR
                        ? "Clôturer le Proposal Round"
                        : "Close Proposal Round"}
                    </button>
                  </div>
                </form>
              ) : (
                <p
                  className="ns-card-meta"
                  style={{
                    margin: 0,
                  }}
                >
                  {isFR
                    ? "Le Proposal Round reste ouvert jusqu’à ce que les contributions en attente soient examinées."
                    : "The Proposal Round stays open until the pending contributions are reviewed."}
                </p>
              )}
            </>
          ) : context ? (
            scenes.length === 0 ? (
              <div
                className="ns-card-meta"
              >
                {isFR
                  ? "Le scénario gelé ne contient encore aucune scène à proposer."
                  : "The frozen screenplay does not yet contain a scene to propose against."}
              </div>
            ) : (
              <>
                <div
                  aria-label="Frozen Canon context"
                  style={{
                    padding: 16,
                    borderRadius: 14,
                    border:
                      "1px solid rgba(15,23,42,0.12)",
                    background:
                      "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "flex-start",
                      gap: 12,
                      flexWrap:
                        "wrap",
                    }}
                  >
                    <div
                      style={{
                        minWidth: 0,
                        flex: "1 1 420px",
                      }}
                    >
                      <strong>
                        {isFR
                          ? "Contexte Canon gelé"
                          : "Frozen Canon context"}
                        {" · "}
                        {isFR
                          ? "Révision"
                          : "Revision"}
                        {" "}
                        {Number(
                          proposalRound
                            ?.scope
                            ?.baseContentRevision
                        )}
                      </strong>

                      <p
                        className="ns-card-meta"
                        style={{
                          margin:
                            "6px 0 0",
                          lineHeight:
                            1.5,
                        }}
                      >
                        {isFR
                          ? "Les scènes Canon récentes sont affichées ici uniquement comme contexte de lecture. Cette fenêtre ne limite pas votre proposition : vous pouvez toujours cibler n’importe quelle scène ou n’importe quel élément du script dans cette révision gelée."
                          : "Recent Canon scenes are shown here as read-only context. This window does not limit your proposal: you may still target any Scene or Script Element in this frozen revision."}
                      </p>
                    </div>

                    {scenes.length >
                    2 ? (
                      <button
                        className="ns-button ns-button-outline"
                        type="button"
                        onClick={() =>
                          setShowExpandedCanonContext(
                            (current) =>
                              !current
                          )
                        }
                      >
                        {showExpandedCanonContext
                          ? (
                              isFR
                                ? "Afficher les deux plus récentes"
                                : "Show recent two"
                            )
                          : (
                              isFR
                                ? "Voir jusqu’à cinq scènes Canon"
                                : "View up to five Canon scenes"
                            )}
                      </button>
                    ) : null}
                  </div>

                  <div
                    style={{
                      display:
                        "grid",
                      gap: 10,
                      marginTop: 12,
                      maxHeight: 520,
                      overflowY:
                        "auto",
                    }}
                  >
                    {visibleCanonContextScenes.map(
                      (scene) => (
                        <article
                          key={
                            scene
                              .sceneId
                          }
                          style={{
                            padding: 13,
                            borderRadius:
                              12,
                            background:
                              "#ffffff",
                            border:
                              "1px solid rgba(15,23,42,0.10)",
                          }}
                        >
                          <strong>
                            {sceneLabel(
                              scene
                            )}
                          </strong>

                          <div
                            style={{
                              display:
                                "grid",
                              gap: 9,
                              marginTop:
                                9,
                            }}
                          >
                            {(
                              Array.isArray(
                                scene
                                  ?.elements
                              )
                                ? scene
                                    .elements
                                : []
                            ).map(
                              (
                                element,
                                index
                              ) => (
                                <div
                                  key={
                                    element
                                      ?.elementId ||
                                    index
                                  }
                                >
                                  <div
                                    style={{
                                      fontSize:
                                        11,
                                      fontWeight:
                                        800,
                                      letterSpacing:
                                        "0.04em",
                                      textTransform:
                                        "uppercase",
                                      color:
                                        "#475569",
                                    }}
                                  >
                                    {humanType(
                                      element
                                        ?.type
                                    )}
                                  </div>

                                  <div
                                    style={{
                                      marginTop:
                                        2,
                                      whiteSpace:
                                        "pre-wrap",
                                      lineHeight:
                                        1.5,
                                    }}
                                  >
                                    {String(
                                      element
                                        ?.text ??
                                        ""
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </article>
                      )
                    )}
                  </div>
                </div>

                <form
                  onSubmit={
                    submitProposal
                  }
                style={{
                  display: "grid",
                  gap: 14,
                }}
              >
                <label>
                  <strong>
                    {isFR
                      ? "Scène"
                      : "Scene"}
                  </strong>

                  <select
                    className="ns-input"
                    value={
                      selectedSceneId
                    }
                    onChange={(
                      event
                    ) =>
                      chooseScene(
                        event.target
                          .value
                      )
                    }
                    style={{
                      display: "block",
                      marginTop: 7,
                      width: "100%",
                      maxWidth: 760,
                    }}
                  >
                    {scenes.map(
                      (scene) => (
                        <option
                          key={
                            scene
                              .sceneId
                          }
                          value={
                            scene
                              .sceneId
                          }
                        >
                          {
                            sceneLabel(
                              scene
                            )
                          }
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label>
                  <strong>
                    {isFR
                      ? "Type de proposition"
                      : "Proposal type"}
                  </strong>

                  <select
                    className="ns-input"
                    value={
                      proposalType
                    }
                    onChange={(
                      event
                    ) =>
                      setProposalType(
                        event.target
                          .value
                      )
                    }
                    style={{
                      display: "block",
                      marginTop: 7,
                      width: "100%",
                      maxWidth: 760,
                    }}
                  >
                    <option value="script_element_revision">
                      {isFR
                        ? "Révision d’un élément du script"
                        : "Script Element revision"}
                    </option>

                    <option value="scene_alternative">
                      {isFR
                        ? "Alternative de scène"
                        : "Scene alternative"}
                    </option>
                  </select>
                </label>

                {proposalType ===
                "script_element_revision" ? (
                  <>
                    <label>
                      <strong>
                        {isFR
                          ? "Élément du script"
                          : "Script Element"}
                      </strong>

                      <select
                        className="ns-input"
                        value={
                          selectedElementId
                        }
                        onChange={(
                          event
                        ) =>
                          chooseElement(
                            event
                              .target
                              .value
                          )
                        }
                        style={{
                          display: "block",
                          marginTop: 7,
                          width: "100%",
                          maxWidth: 760,
                        }}
                      >
                        {(
                          selectedScene
                            ?.elements ||
                          []
                        ).map(
                          (
                            element
                          ) => (
                            <option
                              key={
                                element
                                  .elementId
                              }
                              value={
                                element
                                  .elementId
                              }
                            >
                              {
                                elementLabel(
                                  element
                                )
                              }
                            </option>
                          )
                        )}
                      </select>
                    </label>

                    <label>
                      <strong>
                        {humanType(
                          selectedElement
                            ?.type
                        )}
                      </strong>

                      <textarea
                        className="ns-input"
                        rows={6}
                        value={
                          elementText
                        }
                        disabled={
                          working ||
                          Boolean(
                            selectedOwnSubmission
                          )
                        }
                        onChange={(
                          event
                        ) =>
                          setElementText(
                            event.target
                              .value
                          )
                        }
                        style={{
                          display: "block",
                          marginTop: 7,
                          width: "100%",
                        }}
                      />
                    </label>
                  </>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gap: 11,
                    }}
                  >
                    {sceneDraftElements.map(
                      (
                        element,
                        index
                      ) => (
                        <label
                          key={
                            element
                              .elementId
                          }
                        >
                          <strong>
                            {humanType(
                              element
                                .type
                            )}
                          </strong>

                          <textarea
                            className="ns-input"
                            rows={4}
                            value={
                              element
                                .text
                            }
                            disabled={
                              working ||
                              Boolean(
                                selectedOwnSubmission
                              )
                            }
                            onChange={(
                              event
                            ) =>
                              updateSceneElement(
                                index,
                                event
                                  .target
                                  .value
                              )
                            }
                            style={{
                              display:
                                "block",
                              marginTop:
                                7,
                              width:
                                "100%",
                            }}
                          />
                        </label>
                      )
                    )}
                  </div>
                )}

                <div>
                  <button
                    className={
                      `ns-button ${
                        selectedOwnSubmission
                          ? "ns-button-outline"
                          : "ns-button-primary"
                      }`
                    }
                    type="submit"
                    disabled={
                      working ||
                      Boolean(
                        selectedOwnSubmission
                      ) ||
                      (
                        proposalType ===
                        "script_element_revision"
                          ? !elementChanged
                          : !sceneChanged
                      )
                    }
                  >
                    {selectedOwnSubmission
                      ? (
                          isFR
                            ? "Envoyée"
                            : "Submitted"
                        )
                      : working
                        ? (
                            isFR
                              ? "Envoi…"
                              : "Submitting…"
                          )
                        : (
                            isFR
                              ? "Envoyer la proposition"
                              : "Submit Proposal"
                          )}
                  </button>
                </div>
              </form>
              </>
            )
          ) : null}
        </div>
      )}
    </section>
  );
}
